import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Input from "../../../../../components/common/Input/Input";
import SelectInput from "../../../../../components/common/selectInput/selectInput";
import TelInput from "../../../../../components/common/telInput/telInput";
import useLanguageTranslate from "../../../../../hooks/useLanguageTranslate";
import BottomStickyBtnLayout from "../../../../../pages/motorInsurancePages/newMotorPolicy/Layouts/BottomStickyBtnLayout";
import { updateHomeFormData, selectHomeFormData } from "../../../../../redux/features/homeFormSlice";
import { selectLoginTokenInfo } from "../../../../../redux/features/loginRegisterSlice";
import {
  fetchProfileInfo,
  selectProfileData,
} from "../../../../../redux/features/profileSlice";
import {
  fetchPartyCode,
  selectPartyCode,
} from "../../../../../redux/features/quoteSlice";
import {
  daysOptions,
  monthsOptions,
  yearsOptions,
} from "../../../../../utils/calenderOptions";
import NewHomeJourneyLayout from "../../../layouts/NewHomeJourneyLayout";
import "./basicInfo.css";

const BasicInfo = ({ continueClickHandler, onClose }) => {
  const dispatch = useDispatch();
  const translate = useLanguageTranslate();
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Profile and login data
  const profileData = useSelector(selectProfileData);
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const partyCode = useSelector(selectPartyCode);

  // Get saved form data from Redux
  const savedFormData = useSelector(selectHomeFormData);
  const savedBasicInfo = savedFormData?.HOME_BASIC_INFO_FORM;

  // Check if user is logged in
  const isGuest = !Boolean(loginTokenInfo);

  // Fetch profile data if logged in and not already loaded
  useEffect(() => {
    if (!isGuest && !profileData) {
      dispatch(fetchProfileInfo());
    }
  }, [isGuest, profileData, dispatch]);

  // Fetch party code if logged in and not already loaded
  useEffect(() => {
    if (!isGuest && !partyCode && loginTokenInfo?.cpr) {
      dispatch(fetchPartyCode(loginTokenInfo.cpr));
    }
  }, [isGuest, partyCode, loginTokenInfo?.cpr, dispatch]);

  // Helper function to parse profile date for form
  const parseProfileDateForForm = (dateString) => {
    if (!dateString) return { day: "", month: "", year: "" };
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn("Invalid date string:", dateString);
        return { day: "", month: "", year: "" };
      }
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear().toString();
      return { day, month, year };
    } catch (error) {
      console.error("Error parsing profile date:", error);
      return { day: "", month: "", year: "" };
    }
  };

  // Initialize form with profile data when available (for logged-in users)
  // OR restore from Redux (for guest users who navigated back)
  useEffect(() => {
    // Only initialize once
    if (isInitialized) return;

    if (!isGuest && profileData) {
      // Logged in user - use profile data
      const profileDateOfBirth = parseProfileDateForForm(
        profileData.DateOfBirth || profileData.DOB
      );

      setValues({
        fullName:
          profileData.FirstName && profileData.LastName
            ? `${profileData.FirstName} ${profileData.LastName}`.trim()
            : "",
        email: profileData.Email || "",
        mobile: profileData.PhoneNumber || "",
        day: profileDateOfBirth.day,
        month: profileDateOfBirth.month,
        year: profileDateOfBirth.year,
      });
      setIsInitialized(true);
    } else if (isGuest && savedBasicInfo) {
      // Guest user - restore from Redux if data exists
      const birthDateParts = savedBasicInfo.birthDate?.split("-") || [];
      setValues({
        fullName: savedBasicInfo.fullName || "",
        email: savedBasicInfo.email || "",
        mobile: savedBasicInfo.contactNumber || "",
        year: birthDateParts[0] || "",
        month: birthDateParts[1] || "",
        day: birthDateParts[2] || "",
        fullDate: savedBasicInfo.birthDate || "",
      });
      setIsInitialized(true);
    } else if (isGuest && !savedBasicInfo) {
      // Guest user with no saved data - mark as initialized
      setIsInitialized(true);
    }
  }, [isGuest, profileData, savedBasicInfo, isInitialized]);

  // Custom validation function with specific error messages
  const validateForm = (data) => {
    const formData = data || values;
    const newErrors = {};

    // Full Name validation
    if (!formData.fullName?.trim()) {
      newErrors.fullName = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (!/^[a-zA-Z\u0600-\u06FF\s.]+$/.test(formData.fullName)) {
      newErrors.fullName = translate(
        "Name can only contain alphabetic characters, spaces, and periods.",
        "يجب أن يحتوي الاسم على حروف أبجدية ومسافات ونقاط فقط."
      );
    }

    // Email validation - required first, then format
    if (!formData.email?.trim()) {
      newErrors.email = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = translate(
        "Invalid email address.",
        "البريد الإلكتروني غير صحيح."
      );
    }

    // Mobile validation - required first, then length
    if (!formData.mobile?.trim()) {
      newErrors.mobile = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (formData.mobile.length !== 8) {
      newErrors.mobile = translate(
        "Invalid mobile number.",
        "رقم الهاتف المحمول غير صحيح."
      );
    }

    // Date of birth validation - check if all fields are provided
    if (!formData.day || !formData.month || !formData.year) {
      newErrors.dateOfBirth = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else {
      // Check if date is in the future
      const selectedDate = new Date(
        parseInt(formData.year, 10),
        parseInt(formData.month, 10) - 1,
        parseInt(formData.day, 10)
      );
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        newErrors.dateOfBirth = translate(
          "Date of birth cannot be in the future.",
          "لا يمكن أن يكون تاريخ الميلاد في المستقبل."
        );
      } else {
        // Check if user is at least 18 years old
        const age = today.getFullYear() - selectedDate.getFullYear();
        const monthDifference = today.getMonth() - selectedDate.getMonth();

        // Adjust age if birthday hasn't occurred this year yet
        const isBeforeBirthday =
          monthDifference < 0 ||
          (monthDifference === 0 && today.getDate() < selectedDate.getDate());

        const actualAge = isBeforeBirthday ? age - 1 : age;

        if (actualAge < 18) {
          newErrors.dateOfBirth = translate(
            "You must be at least 18 years old.",
            "يجب أن تكون 18 عامًا على الأقل."
          );
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formSubmitHandler = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Save basic info to Redux
      dispatch(
        updateHomeFormData({
          formName: "HOME_BASIC_INFO_FORM",
          data: {
            fullName: values.fullName,
            email: values.email,
            contactNumber: values.mobile,
            birthDate: `${values.year}-${values.month?.padStart(
              2,
              "0"
            )}-${values.day?.padStart(2, "0")}`,
          },
        })
      );
      continueClickHandler();
    }
  };

  // Helper function to save form data to Redux
  const saveToRedux = (formValues) => {
    dispatch(
      updateHomeFormData({
        formName: "HOME_BASIC_INFO_FORM",
        data: {
          fullName: formValues.fullName || "",
          email: formValues.email || "",
          contactNumber: formValues.mobile || "",
          birthDate: formValues.year && formValues.month && formValues.day
            ? `${formValues.year}-${formValues.month?.padStart(2, "0")}-${formValues.day?.padStart(2, "0")}`
            : "",
        },
      })
    );
  };

  // Mobile date picker handler
  const handleMobileDatePickerSelect = (dateString) => {
    if (!dateString) {
      const cleared = { ...values, day: "", month: "", year: "", fullDate: "" };
      setValues(cleared);
      if (isInitialized) {
        saveToRedux(cleared);
      }
      if (errors?.dateOfBirth) {
        validateForm(cleared);
      }
      return;
    }

    // Parse the date string (YYYY-MM-DD format from mobile date picker)
    const [year, month, day] = dateString.split("-");

    const update = {
      ...values,
      day: day,
      month: month,
      year: year,
      fullDate: dateString,
    };

    setValues(update);
    // Only save to Redux after initialization to avoid overwriting restored data
    if (isInitialized) {
      saveToRedux(update);
    }

    // Clear date error when user selects a date
    if (errors?.dateOfBirth) {
      validateForm(update);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let update = { ...values, [name]: value };
    setValues(update);
    // Only save to Redux after initialization to avoid overwriting restored data
    if (isInitialized) {
      saveToRedux(update);
    }
    if (errors?.[name] || errors?.dateOfBirth) {
      validateForm(update);
    }
  };

  return (
    <div className="basic-info-page w-full">
      <NewHomeJourneyLayout
        show_close_btn={true}
        is_hide_back_btn={true}
        onClose={onClose}
        isFirstPage={true}
      >
        <div className="content min-w-[32%] max767:w-full">
          <h1 className="text-[#1A2024] text-2xl not-italic font-semibold leading-8 tracking-[-0.24px] text-center max767:text-left max767:rtl:text-right">
            {translate("New Home Insurance", "تأمين المنزل")}
          </h1>
          <p className="text-[#1A2024] text-base not-italic font-normal leading-6 tracking-[-0.16px] text-center mt-1 mb-6 max767:text-left max767:rtl:text-right">
            {translate(
              "Welcome! Let's start with some basic information.",
              "مرحبًا! دعنا نبدأ ببعض المعلومات الأساسية."
            )}
          </p>
        </div>
        <form onSubmit={formSubmitHandler} className="basic-info-form w-full">
          <BottomStickyBtnLayout
            show_summary={false}
            hide_back_btn={true}
            btn_label={translate("Continue", "التالي")}
            btn_type="submit"
            btn_onClick={formSubmitHandler}
            passedFrom="HomeInsurance"
          >
            <div className="w-[32%] max767:w-full mx-auto ipad:w-[60%] space-y-6">
              <Input
                label={translate("Full Name", "الاسم الكامل")}
                type="text"
                name="fullName"
                value={values.fullName || ""}
                error={errors.fullName}
                handleChange={handleChange}
              />
              <Input
                label={translate("Email Address", "عنوان البريد الإلكتروني")}
                type="email"
                name="email"
                value={values.email || ""}
                error={errors.email}
                handleChange={handleChange}
              />
              <TelInput
                name="mobile"
                value={values.mobile || ""}
                error={errors.mobile}
                onChange={handleChange}
                label={translate("Mobile Number", "رقم الهاتف المحمول")}
                className="mobilenumber px-4 py-3 max767:w-[50%] w-[97%] border-r-0 border-0 rounded-[9px] h-[46px] focus:outline-none"
              />
              <div className="date_of_birth">
                  <label className="block mb-[5px] text-[#48535B] not-italic font-normal leading-6 text-base max-350:text-[13px]">
                    {translate("Date of Birth", "تاريخ الميلاد")}
                  </label>

                  {/* Mobile: native HTML5 date input (visible <768px) */}
                  <div className="block md:hidden">
                    <input
                      type="date"
                      value={`${values.year && values.month && values.day ? `${values.year}-${String(values.month).padStart(2,"0")}-${String(values.day).padStart(2,"0")}` : ""}`}
                      onChange={(e) => handleMobileDatePickerSelect(e.target.value)}
                      className={`w-full h-[50px] px-4 py-3 border rounded-[10px] bg-white text-base focus:outline-none focus:border-[#0891B2] ${errors.dateOfBirth ? "border-red-500" : "border-[#B0BABF]"}`}
                      style={{ fontSize: "16px", WebkitAppearance: "none", appearance: "none" }}
                    />
                    {errors.dateOfBirth && (
                      <div className="mt-2 text-red-600 text-sm">
                        {errors.dateOfBirth}
                      </div>
                    )}
                  </div>

                  {/* Desktop: Day/Month/Year dropdowns (visible >=768px) */}
                  <div className="hidden md:block">
                    <div className="flex gap-6 self-stretch rtl:flex-row">
                      <SelectInput
                        name="day"
                        options_data={daysOptions}
                        formData={values}
                        default_placeholder={translate("Day", "يوم")}
                        handleChange={handleChange}
                        maxMenuHeight={185}
                      />

                      <SelectInput
                        name="month"
                        formData={values}
                        default_placeholder={translate("Month", "شهر")}
                        options_data={monthsOptions}
                        handleChange={handleChange}
                        maxMenuHeight={185}
                      />

                      <SelectInput
                        name="year"
                        formData={values}
                        default_placeholder={translate("Year", "سنة")}
                        options_data={yearsOptions}
                        handleChange={handleChange}
                        maxMenuHeight={185}
                      />
                    </div>

                    {errors.dateOfBirth && (
                      <div className="mt-2 text-red-600 text-sm">
                        {errors.dateOfBirth}
                      </div>
                    )}
                  </div>
                </div>
              </div>
          </BottomStickyBtnLayout>
        </form>
      </NewHomeJourneyLayout>
    </div>
  );
};

export default BasicInfo;
